<script lang="ts" setup>
import { ref } from 'vue';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import useFlowSteps from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';

// Import stores
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';

// Import used components
import { FlowLayout } from '@Shared';

// Import images
import houseTargetIcon from '@Assets/images/house-target.svg';
import chartTrendIcon from '@Assets/images/chart-trend.svg';
import checkIcon from '@Assets/images/check.svg';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const { advanceFlow, goBackInFlow } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { taskNavTitle } = useTaskNavTitle();

// Page state
const isNavigating = ref(false);

// Actions
const continueFlow = () => {
    isNavigating.value = true;
    advanceFlow();
};
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <app-form-content-layout>
            <h1 class="hero-package__headline" bottomOffset="1rem">
                Know you're covered
            </h1>

            <p class="hero-package__description">
                We are committed to keeping your funds safe. To do so,
                {{ companyStore.company?.name ?? "your closing team" }} has
                included CertifID's money protection plan for your closing.
            </p>

            <!-- Wire Fraud Statistics Card -->
            <div class="hero-package__stats-card">
                <h2 class="hero-package__stats-title">
                    Wire fraud is on the rise
                </h2>

                <div class="hero-package__stat-group">
                    <div class="hero-package__stat">
                        <img
                            :src="houseTargetIcon"
                            alt=""
                            class="hero-package__stat-icon"
                        />
                        <div class="hero-package__stat-content">
                            <span class="hero-package__stat-text">
                                <strong>1 in 3</strong> real estate transactions
                                are targeted by wire fraud
                            </span>
                        </div>
                    </div>
                    <p class="hero-package__stat-source">
                        ALTA 2021 Wire Fraud Survey
                    </p>
                </div>

                <div class="hero-package__stat-group">
                    <div class="hero-package__stat">
                        <img
                            :src="chartTrendIcon"
                            alt=""
                            class="hero-package__stat-icon"
                        />
                        <div class="hero-package__stat-content">
                            <span class="hero-package__stat-text">
                                <strong>+300%</strong> average total loss
                                increase 2016 to 2022
                            </span>
                        </div>
                    </div>
                    <p class="hero-package__stat-source">2021 FBI IC3 Report</p>
                </div>
            </div>

            <!-- Coverage Benefits Section -->
            <div class="hero-package__benefits">
                <div class="hero-package__benefits-header">
                    Your Coverage Benefits
                </div>

                <div class="hero-package__benefits-content">
                    <div class="hero-package__benefit-section">
                        <h3 class="hero-package__benefit-title">
                            Funds recovery assistance
                        </h3>
                        <p class="hero-package__benefit-description">
                            If funds are sent to a fraudulent account,
                            CertifID's fraud recovery experts will support you
                            by:
                        </p>
                        <ul class="hero-package__benefit-list">
                            <li class="hero-package__benefit-item">
                                <img
                                    :src="checkIcon"
                                    alt=""
                                    class="hero-package__check-icon"
                                />
                                <span
                                >Working with your bank to initiate
                                    recovery</span
                                >
                            </li>
                            <li class="hero-package__benefit-item">
                                <img
                                    :src="checkIcon"
                                    alt=""
                                    class="hero-package__check-icon"
                                />
                                <span>Coordinating with law enforcement</span>
                            </li>
                            <li class="hero-package__benefit-item">
                                <img
                                    :src="checkIcon"
                                    alt=""
                                    class="hero-package__check-icon"
                                />
                                <span
                                >Providing updates and guidance throughout
                                    the recovery process</span
                                >
                            </li>
                        </ul>
                    </div>

                    <div class="hero-package__benefit-section">
                        <h3 class="hero-package__benefit-title">
                            CertifID's dedicated support
                        </h3>
                        <p class="hero-package__benefit-description">
                            Sending wire transfers can be complicated, if you
                            have questions, the CertifID team of experts are
                            just a phone call, email or chat away.
                        </p>
                    </div>
                </div>
            </div>

            <app-button
                expanded
                size="large"
                @onClick="continueFlow"
                :is-loading="isNavigating"
                data-id="hero-package-continue"
            >
                Continue
            </app-button>
        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.hero-package {
    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-med;
        margin: 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-med;
        margin: 0;
    }

    &__stats-card {
        background: $white;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
        padding: 1rem;
    }

    &__stats-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: $black-med;
        margin: 0 0 1.5rem 0;
    }

    &__stat {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
    }

    &__stat-icon {
        flex-shrink: 0;
        width: 3rem;
        height: 3rem;
    }

    &__stat-content {
        display: flex;
        flex-direction: column;
    }

    &__stat-text {
        font-size: 1rem;
        color: $black-low;
        line-height: 150%;

        strong {
            color: $black-low;
            font-weight: 700;
        }
    }

    &__stat-group {
        display: flex;
        flex-direction: column;
        margin-bottom: 1.5rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__stat-source {
        font-size: 0.625rem;
        color: $black-low;
        margin: 0;
        padding: 0;
    }

    &__benefits {
        background: $white;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
        overflow: hidden;
    }

    &__benefits-header {
        background: var(--ch-primary-dark);
        color: $white;
        font-size: 0.9rem;
        font-weight: 700;
        text-align: center;
        padding: 0.75rem;
    }

    &__benefits-content {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    &__benefit-section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    &__benefit-title {
        font-size: 1rem;
        font-weight: 700;
        color: $black-med;
        margin: 0;
    }

    &__benefit-description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-med;
        margin: 0;
    }

    &__benefit-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    &__benefit-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1rem;
        line-height: 150%;
        color: $black-med;
    }

    &__check-icon {
        flex-shrink: 0;
        width: 1rem;
        height: 1rem;
    }
}
</style>
